/* Feb-2026 · Manuscript Toolbox drawer — a thin horizontal handle
 * sits right below the menu bar; hovering it with a mouse slides the
 * full typography/spell-check toolbar down from underneath. Tapping
 * it on touch/tablet toggles it open/closed independently of hover
 * (touch devices fire a compatibility mouseenter right before click,
 * which used to fight with the toggle and both lock touch users out
 * and make a deliberate desktop click close an already-open drawer).
 * A pin button in the handle itself keeps it open regardless of
 * hover/tap state; pin state is remembered across sessions.
 */
import React, { useState } from 'react';
import { Wrench, ChevronDown, Pin } from 'lucide-react';
import { ManuscriptToolbar } from './ManuscriptToolbar';

function loadPinned() {
  try { return window.localStorage.getItem('pw_toolbox_pinned') === '1'; } catch { return false; }
}

export function ManuscriptToolboxDrawer(props) {
  const [pinned, setPinned] = useState(loadPinned);
  const [hovering, setHovering] = useState(false);   // real mouse hover only
  const [tapOpen, setTapOpen] = useState(false);     // click/tap toggle
  const open = pinned || hovering || tapOpen;

  const togglePin = () => {
    setPinned((p) => {
      const next = !p;
      try { window.localStorage.setItem('pw_toolbox_pinned', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div
      className="relative z-20 shrink-0 border-b border-panel-border bg-panel-header/20"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovering(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovering(false); }}
      data-testid="manuscript-toolbox-rail"
    >
      {/* Handle — full-width thin bar directly below the menu bar. */}
      <div className="relative flex h-5 w-full items-center justify-center">
        <button
          type="button"
          onClick={() => setTapOpen((o) => !o)}
          data-testid="manuscript-toolbox-tab"
          title="Toolbox — typography, spacing & spell-check"
          className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-primary"
        >
          <Wrench className="h-3 w-3" />
          <span>Toolbox</span>
          <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        <button
          type="button"
          onClick={togglePin}
          data-testid="manuscript-toolbox-pin"
          title={pinned ? 'Unpin toolbox' : 'Pin toolbox open'}
          aria-pressed={pinned}
          className={`absolute right-1.5 flex h-4 w-4 items-center justify-center rounded-sm transition-colors ${
            pinned ? 'text-primary' : 'text-muted-foreground hover:text-primary'
          }`}
        >
          <Pin className="h-3 w-3" />
        </button>
      </div>

      {/* Sliding panel — height-animated, in document flow (pushes the
          manuscript down while open, rather than overlaying it). */}
      <div
        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${open ? 'max-h-40' : 'max-h-0'}`}
        data-testid="manuscript-toolbox-panel"
      >
        <ManuscriptToolbar {...props} />
      </div>
    </div>
  );
}
